import axios from 'axios';
import Service from '../Service/Service';

// Use same API base as rest of app (backend fetches rate once a day and exposes it here)
const API_BASE_URL = Service.baseURL || 'http://localhost:3072';

// Cache for exchange rates — backend updates once per day, so we cache 24h to match
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000;
let exchangeRateCache = {
  rate: null,
  rateUsd: null,
  timestamp: null,
  CACHE_DURATION: CACHE_DURATION_MS
};

/**
 * Fetch GBP and USD to PKR exchange rates from backend API.
 */
export const getExchangeRates = async () => {
  try {
    const now = Date.now();
    if (exchangeRateCache.rate != null &&
        exchangeRateCache.rateUsd != null &&
        exchangeRateCache.timestamp != null &&
        (now - exchangeRateCache.timestamp) < exchangeRateCache.CACHE_DURATION) {
      return { rate: exchangeRateCache.rate, rateUsd: exchangeRateCache.rateUsd };
    }

    const response = await axios.get(`${API_BASE_URL}/api/exchange-rate`);

    if (response.data?.success && response.data?.rate != null) {
      exchangeRateCache.rate = parseFloat(response.data.rate);
      exchangeRateCache.rateUsd = response.data?.rateUsd != null ? parseFloat(response.data.rateUsd) : 280;
      exchangeRateCache.timestamp = now;
      return { rate: exchangeRateCache.rate, rateUsd: exchangeRateCache.rateUsd };
    }

    console.warn('Exchange rate API did not return rates, using fallback');
    if (exchangeRateCache.rate == null) exchangeRateCache.rate = 350;
    if (exchangeRateCache.rateUsd == null) exchangeRateCache.rateUsd = 280;
    return { rate: exchangeRateCache.rate, rateUsd: exchangeRateCache.rateUsd };
  } catch (error) {
    console.error('Error fetching exchange rate from API:', error);
    if (exchangeRateCache.rate == null) exchangeRateCache.rate = 350;
    if (exchangeRateCache.rateUsd == null) exchangeRateCache.rateUsd = 280;
    return { rate: exchangeRateCache.rate, rateUsd: exchangeRateCache.rateUsd };
  }
};

/** @deprecated Use getExchangeRates. Returns GBP to PKR rate only. */
export const getExchangeRate = async () => {
  const { rate } = await getExchangeRates();
  return rate;
};

/**
 * Convert GBP amount to PKR
 */
export const convertToPKR = async (gbpAmount) => {
  if (!gbpAmount || isNaN(gbpAmount)) return 0;
  const { rate } = await getExchangeRates();
  const pkrAmount = parseFloat(gbpAmount) * rate;
  return Math.round(pkrAmount * 100) / 100;
};

/**
 * Convert amount from given currency to PKR (GBP or USD supported).
 */
export const convertToPKRFrom = async (amount, fromCurrency) => {
  if (!amount || isNaN(amount)) return 0;
  const num = parseFloat(amount);
  const { rate, rateUsd } = await getExchangeRates();
  const pkr = fromCurrency === 'USD' ? num * rateUsd : num * rate;
  return Math.round(pkr * 100) / 100;
};

/**
 * Convert amount to GBP for display (USD amounts are converted using exchange rates).
 */
export const convertToGBP = async (amount, fromCurrency = 'GBP') => {
  if (!amount || isNaN(amount)) return 0;
  const num = parseFloat(amount);
  if (fromCurrency === 'GBP') return Math.round(num * 100) / 100;
  if (fromCurrency === 'USD') {
    const { rate, rateUsd } = await getExchangeRates();
    const gbp = (rateUsd && rate) ? num * rateUsd / rate : num;
    return Math.round(gbp * 100) / 100;
  }
  if (fromCurrency === 'PKR') {
    const { rate } = await getExchangeRates();
    const gbp = rate ? num / rate : num;
    return Math.round(gbp * 100) / 100;
  }
  return Math.round(num * 100) / 100;
};

/** Markup applied to flight prices when displaying in PKR (10%) */
export const FLIGHT_PRICE_MARKUP_PERCENT = 10;

/**
 * Convert to PKR and apply markup; returns final PKR amount to display.
 */
export const convertToPKRWithMarkup = async (amount, fromCurrency = 'GBP') => {
  const pkr = await convertToPKRFrom(amount, fromCurrency === 'USD' ? 'USD' : 'GBP');
  const withMarkup = pkr * (1 + FLIGHT_PRICE_MARKUP_PERCENT / 100);
  return Math.round(withMarkup * 100) / 100;
};

/**
 * Format PKR amount in compact form: 10K, 100K, 1M, 1.5M, etc.
 */
export const formatPkrCompact = (pkrAmount) => {
  if (pkrAmount == null || isNaN(pkrAmount)) return '0';
  const num = Math.round(parseFloat(pkrAmount));
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return num.toLocaleString();
};

/**
 * Format currency with proper symbols
 */
export const formatCurrency = (amount, currency = 'GBP') => {
  if (amount == null || amount === '' || isNaN(amount) || parseFloat(amount) === 0) return '-';
  
  // Apply 40% reduction to the displayed amount, but ensure it doesn't show as 0 if the original amount was > 0
  let numAmount = parseFloat(amount) * 0.6;
  if (parseFloat(amount) > 0 && numAmount < 0.01) {
    numAmount = 0.01; // Minimum displayable amount
  }
  
  if (currency === 'PKR') {
    return `Rs ${numAmount.toLocaleString('en-PK', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    })}`;
  } else if (currency === 'USD') {
    return `$${numAmount.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  } else if (currency === 'GBP') {
    return `£${numAmount.toLocaleString('en-GB', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  } else if (currency && String(currency).length >= 3) {
    return `${currency} ${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } else {
    return `£${numAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
};
